// ProfilePage - read-only account view: identity and org memberships.

import {
  Avatar,
  DescriptionList,
  DTRow,
  PageHeader,
  Section,
} from '@/components/ui';
import { useAuth } from '@/context/AuthContext';

export default function ProfilePage() {
  const { user } = useAuth();
  if (!user) return null;

  const memberships = user.memberships ?? [];

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader title="Your profile" description="Account details and organizations." />

      <div className="space-y-6">
        {/* Identity card */}
        <Section title="Identity">
          <div className="flex items-center gap-4 mb-5">
            <Avatar name={user.display_name || user.email} size="lg" />
            <div>
              <div className="text-base font-semibold text-ink-100">{user.display_name}</div>
              <div className="text-sm text-ink-400">{user.email}</div>
            </div>
          </div>
          <DescriptionList>
            <DTRow label="Display name">{user.display_name}</DTRow>
            <DTRow label="Email">{user.email}</DTRow>
            <DTRow label="Role">
              {user.is_admin ? (
                <span className="pill border-accent-700/40 text-accent-300 bg-accent-500/5">Administrator</span>
              ) : (
                <span className="pill border-ink-700 text-ink-400">Member</span>
              )}
            </DTRow>
          </DescriptionList>
        </Section>

        {/* Organizations */}
        <Section
          title="Organizations"
          description={memberships.length === 1 ? '1 organization' : `${memberships.length} organizations`}
        >
          {memberships.length === 0 ? (
            <div className="text-sm text-ink-500 text-center py-6">
              You aren't a member of any organization yet.
            </div>
          ) : (
            <div className="divide-y divide-ink-800/60 -mx-6 -mb-6">
              {memberships.map((m) => (
                <div key={m.org_id} className="flex items-center gap-3 px-6 py-3">
                  <Avatar name={m.org_name} size="sm" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-ink-100 font-medium truncate">{m.org_name}</div>
                    <div className="text-[11px] text-ink-500 capitalize">
                      {m.role.replace('_', ' ')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}
